/**
 * Equipe da incorporadora: quem tem acesso e quem foi convidado.
 *
 * ⚠ O TOKEN DO CONVITE APARECE UMA VEZ SÓ, E ISSO É DE PROPÓSITO.
 * O banco guarda apenas `sha256(token)`. Quem emite o convite vê o link nesta tela e
 * precisa copiá-lo ali; fechar sem copiar significa revogar e emitir outro. A alternativa
 * — guardar o token em claro para poder remostrar — transformaria a tabela de convites
 * numa lista de chaves de acesso prontas para uso.
 *
 * NÃO HÁ ENVIO DE E-MAIL. O link é copiado e enviado por quem convida, pelo canal que
 * quiser. É uma limitação real e está dita na tela, para ninguém ficar esperando um
 * e-mail que não vem.
 */
import { h, dataCurta } from '../dom';
import { icone } from '../icones';
import { cabecalhoDeTela, tentar, vazio } from '../casca';
import type { Vista } from '../casca';
import { avisar, confirmar, perguntar } from '../ui/aviso';
import { recarregar } from '../rotas';
import {
  PAPEIS, alterarPapelMembro, convidarUsuario, definirMembroAtivo, listarConvites,
  listarEmpresas, listarMembros, revogarConvite,
} from '../../../../packages/dados/src/repositorios';
import type { ConvitePendente, Membro, Papel } from '../../../../packages/dados/src/repositorios';
import { podeAdministrar } from '../../../../packages/dados/src/sessao';
import type { Perfil } from '../../../../packages/dados/src/sessao';
import { seloDoMembro } from '../acesso-plataforma';

const NOME_PAPEL: Record<Papel, string> = {
  gestor: 'Gestor',
  analista: 'Analista',
  consulta: 'Consulta',
};

const O_QUE_FAZ: Record<Papel, string> = {
  gestor: 'Edita tudo e administra a equipe.',
  analista: 'Edita cenários, mas não mexe na equipe.',
  consulta: 'Só visualiza.',
};

/** O endereço que o convidado vai abrir. */
export function enderecoDoConvite(token: string, email: string): string {
  const u = new URL(`/convite/${encodeURIComponent(token)}`, window.location.origin);
  /* O e-mail viaja junto para o cadastro poder travá-lo. Sem isso, quem digitasse um
     e-mail diferente do convidado receberia um erro 500 opaco do servidor de login —
     o gatilho recusa, mas a mensagem não atravessa. */
  u.searchParams.set('e', email);
  return u.toString();
}

function linhaMembro(m: Membro, empresaId: string, souEu: boolean, souAdmin: boolean, podeMexer: boolean): HTMLElement {
  /* ADM-4 (revisão de 12/10/2026): no PRÓPRIO cartão, o Admin aparece como "Admin Fincast" (o papel do
     vínculo vai no title) — desde a 0031 o que vale para ele são os poderes de plataforma, não o
     "Consulta" do vínculo. O seletor de papel já ficava desabilitado ali (o banco recusa mexer no próprio
     vínculo): no lugar dele, o selo.
     PENDÊNCIA ADM-4 (aberta em 12/10/2026): os OUTROS membros que sejam admin seguem com o papel do
     vínculo — `listarMembros` não traz quem é admin. Fechar pede (1) decidir se a equipe do cliente pode
     ver quem é Admin Fincast (hoje nenhuma tela mostra o de outra pessoa), (2) trazer
     `usuario.is_admin_hype` no embed de `listarMembros` e conferir NO BANCO o que `usuario_sel`
     (`colega_visivel`) e o grant de coluna devolvem, e (3) trocar `souEu && souAdmin` por `m.ehAdmin`. */
  const comoAdmin = souEu && souAdmin;
  const selo = seloDoMembro(NOME_PAPEL[m.papel], comoAdmin);
  /* `souEu` desabilita as ações no PRÓPRIO cartão porque a política do banco recusa
     (`usuario_id <> auth.uid()` em membro_upd e membro_del). Sem isto o botão existiria,
     falharia com "sem permissão" e pareceria defeito — quando é proteção: um gestor que
     se rebaixasse deixaria a incorporadora sem administrador, e sem ninguém que pudesse
     desfazer. */
  const gerenciavel = podeMexer && !souEu;

  const trocarPapel = h('select', {
    class: 'input',
    style: 'width:auto;height:30px;font-size:var(--fs-2xs)',
    disabled: !gerenciavel,
    title: souEu ? 'Você não pode mudar o seu próprio papel.' : 'Trocar o papel',
    onchange: (ev: Event) => {
      const novo = (ev.target as HTMLSelectElement).value as Papel;
      void tentar(
        () => alterarPapelMembro(empresaId, m.usuarioId, novo),
        () => { avisar(`${m.nome || m.email} agora é ${NOME_PAPEL[novo]}.`); recarregar(); },
      ).then(() => { /* em caso de erro a lista não recarrega: devolve o seletor ao valor real */
        (ev.target as HTMLSelectElement).value = m.papel;
      });
    },
  }, ...PAPEIS.map((p) => h('option', { value: p, selected: p === m.papel }, NOME_PAPEL[p])));

  const alternarAcesso = h('button', {
    class: m.ativo ? 'btn sm danger' : 'btn sm out',
    disabled: !gerenciavel,
    /* `null`, não `''`: o `h()` omite o atributo quando é nulo, e um `title=""` no DOM
       além de não dizer nada ainda SUPRIME o tooltip de qualquer ancestral. */
    title: souEu ? 'Você não pode desativar o seu próprio acesso.' : null,
    onclick: () => {
      void (async () => {
        if (m.ativo) {
          const ok = await confirmar({
            titulo: 'Desativar o acesso?',
            corpo: [h('b', {}, m.nome || m.email), ' deixa de ver esta incorporadora. Nada do que essa pessoa fez é apagado, e o acesso pode ser religado depois.'],
            confirmar: 'Desativar',
            perigo: true,
          });
          if (!ok) return;
        }
        await tentar(
          () => definirMembroAtivo(empresaId, m.usuarioId, !m.ativo),
          () => { avisar(m.ativo ? 'Acesso desativado.' : 'Acesso religado.'); recarregar(); },
        );
      })();
    },
  }, m.ativo ? 'Desativar' : 'Religar');

  return h(
    'div',
    { class: 'card', style: 'padding:14px 18px;display:flex;align-items:center;gap:12px' },
    h('span', { style: `color:hsl(var(--${m.ativo ? 'primary-text' : 'muted-foreground'}));flex:none`, html: icone.olho(18) }),
    h(
      'div',
      { style: 'display:grid;gap:2px;min-width:0;flex:1' },
      h('span', { style: `font-weight:620${m.ativo ? '' : ';opacity:.55'}` },
        (m.nome === '' ? m.email : m.nome) + (souEu ? ' (você)' : '')),
      h('span', { class: 'muted', style: 'font-size:var(--fs-2xs)' },
        `${m.email} · desde ${dataCurta(m.desde)}`),
    ),
    m.ativo ? null : h('span', { class: 'badge bad' }, 'Desativado'),
    podeMexer && !comoAdmin ? trocarPapel : h('span', { class: 'badge sec', title: selo.titulo }, selo.texto),
    podeMexer ? alternarAcesso : null,
  );
}

function linhaConvite(c: ConvitePendente, podeMexer: boolean): HTMLElement {
  const vencido = new Date(c.expiraEm).getTime() < Date.now();
  return h(
    'div',
    { class: 'card', style: 'padding:14px 18px;display:flex;align-items:center;gap:14px' },
    h('span', { class: 'muted', style: 'flex:none', html: icone.alerta(17) }),
    h(
      'div',
      { style: 'display:grid;gap:2px;min-width:0;flex:1' },
      h('span', { style: 'font-weight:620' }, c.email),
      h('span', { class: 'muted', style: 'font-size:var(--fs-2xs)' },
        vencido ? `venceu em ${dataCurta(c.expiraEm)}` : `vence em ${dataCurta(c.expiraEm)}`),
    ),
    h('span', { class: 'badge out' }, NOME_PAPEL[c.papel]),
    vencido ? h('span', { class: 'badge warn' }, 'Vencido') : h('span', { class: 'badge sec' }, 'Aguardando'),
    podeMexer
      ? h('button', {
          class: 'btn sm danger',
          onclick: () => {
            void (async () => {
              const ok = await confirmar({
                titulo: 'Revogar o convite?',
                corpo: ['O link enviado para ', h('b', {}, c.email), ' deixa de funcionar. Você pode convidar de novo depois.'],
                confirmar: 'Revogar',
                perigo: true,
              });
              if (!ok) return;
              await tentar(() => revogarConvite(c.id), () => {
                avisar('Convite revogado.');
                recarregar();
              });
            })();
          },
        }, 'Revogar')
      : null,
  );
}

/**
 * Mostra o link recém-criado. Modal próprio, e não toast: é a única vez que este texto
 * existe, e um toast que some sozinho depois de 3 s perderia o convite.
 */
function mostrarLink(email: string, link: string): void {
  const campo = h('input', { class: 'input', value: link, readonly: true, style: 'font-size:var(--fs-2xs)' });
  const copiar = h('button', { class: 'btn pri' }, 'Copiar link');
  copiar.addEventListener('click', () => {
    campo.select();
    void navigator.clipboard.writeText(link).then(
      () => { copiar.textContent = 'Copiado'; },
      () => {
        /* Sem permissão de área de transferência: o campo já está selecionado, e Ctrl+C
           resolve. Falhar calado aqui deixaria o botão parecendo quebrado. */
        copiar.textContent = 'Use Ctrl+C';
      },
    );
  });

  const fundo = h('div', { class: 'ov show' },
    h('div', { class: 'modal cfm', style: 'width:min(560px,94vw)' },
      h('div', { class: 'mh' },
        h('div', {}, h('h3', {}, 'Convite criado')),
        h('button', {
          class: 'iconbtn', style: 'margin-left:auto', title: 'Fechar',
          onclick: () => fundo.remove(),
        }, '✕')),
      h('div', { class: 'cfm-bd', style: 'display:grid;gap:12px' },
        h('p', { style: 'margin:0;line-height:1.55' },
          `Mande este link para ${email}. Ele vale por 7 dias.`),
        campo,
        h('p', { class: 'muted', style: 'margin:0;font-size:var(--fs-2xs);line-height:1.5' },
          'Guarde agora: por segurança o link não fica salvo e não pode ser mostrado de novo. ' +
          'Se perder, revogue o convite e crie outro.')),
      h('div', { class: 'cfm-ft' },
        copiar,
        h('button', { class: 'btn out', onclick: () => fundo.remove() }, 'Fechar'))));

  document.body.appendChild(fundo);
  campo.select();
}

/** Pergunta e-mail e papel. Duas perguntas, um passo só. */
async function pedirConvite(): Promise<{ email: string; papel: Papel } | null> {
  const email = await perguntar({
    titulo: 'Convidar para a equipe',
    rotulo: 'E-mail de quem vai receber o acesso',
    dica: 'pessoa@incorporadora.com.br',
    confirmar: 'Continuar',
  });
  if (email === null) return null;

  return new Promise((resolve) => {
    let papel: Papel = 'analista';
    const opcoes = PAPEIS.map((p) =>
      h('button', {
        class: 'card acard',
        style: 'padding:12px 14px;display:grid;gap:2px;text-align:left;cursor:pointer',
        onclick: () => {
          papel = p;
          for (const b of opcoes) b.classList.toggle('sel', b === opcoes[PAPEIS.indexOf(p)]);
        },
      },
        h('span', { style: 'font-weight:620' }, NOME_PAPEL[p]),
        h('span', { class: 'muted', style: 'font-size:var(--fs-2xs)' }, O_QUE_FAZ[p])));
    opcoes[1]?.classList.add('sel');

    const fechar = (r: { email: string; papel: Papel } | null) => { fundo.remove(); resolve(r); };
    const fundo = h('div', { class: 'ov show' },
      h('div', { class: 'modal cfm' },
        h('div', { class: 'mh' }, h('div', {}, h('h3', {}, `Que acesso ${email} terá?`)),
          h('button', { class: 'iconbtn', style: 'margin-left:auto', onclick: () => fechar(null) }, '✕')),
        h('div', { class: 'cfm-bd', style: 'display:grid;gap:8px' }, ...opcoes),
        h('div', { class: 'cfm-ft' },
          h('button', { class: 'btn out', onclick: () => fechar(null) }, 'Cancelar'),
          h('button', { class: 'btn pri', onclick: () => fechar({ email, papel }) }, 'Criar convite'))));
    document.body.appendChild(fundo);
  });
}

export async function telaEquipe(perfil: Perfil, empresaId: string): Promise<Vista> {
  const [empresas, membros, convites] = await Promise.all([
    listarEmpresas(),
    listarMembros(empresaId),
    listarConvites(empresaId),
  ]);
  const empresa = empresas.find((e) => e.id === empresaId);
  if (empresa === undefined) throw new Error('Esta incorporadora não existe mais, ou saiu do seu acesso.');

  /* Convidar é gestor OU Admin. A política `convite_ins` inclui a plataforma de
     propósito: sem isso, uma incorporadora recém-criada nunca ganharia o primeiro
     gestor — ninguém dentro dela existe para convidá-lo. (Convidar o PRÓPRIO e-mail o
     banco recusa para todo mundo — CONVITE_PARA_SI, 0031/D21.) */
  const podeConvidar = podeAdministrar(perfil, empresaId, empresa.ativa) || perfil.isAdminHype;
  /* Trocar papel e desativar: gestor OU Admin, desde a 0031 (`emp_admin()` passou a incluir
     o Admin em toda incorporadora ativa; o D2-1 foi revogado em 11–12/10/2026). O banco
     continua barrando mexer no PRÓPRIO vínculo — o cartão "(você)" fica sem ações — e
     deixar a incorporadora sem gestor (`chk_gestor`, que não conta o Admin). `empresa.ativa`
     vem desta lista, lida agora: vale sobre o perfil em memória. */
  const podeGerir = podeAdministrar(perfil, empresaId, empresa.ativa);

  const botaoConvidar = (): HTMLElement | null =>
    podeConvidar
      ? h('button', {
          class: 'btn pri',
          'data-el': 'EL-182',
          onclick: () => {
            void (async () => {
              const pedido = await pedirConvite();
              if (pedido === null) return;
              await tentar(
                () => convidarUsuario({ empresaId, email: pedido.email, papel: pedido.papel }),
                (emitido) => {
                  mostrarLink(emitido.convite.email, enderecoDoConvite(emitido.token, emitido.convite.email));
                  recarregar();
                },
              );
            })();
          },
        }, h('span', { html: icone.mais(15) }), 'Convidar alguém')
      : null;

  const corpo = h('div', { 'data-el': 'EL-181' });
  corpo.appendChild(
    cabecalhoDeTela(
      `Equipe · ${empresa.nome}`,
      'Quem tem acesso aos empreendimentos e cenários desta incorporadora.',
      botaoConvidar(),
    ),
  );

  corpo.appendChild(h('div', { class: 'aviso', style: 'margin-bottom:16px;display:flex;gap:9px;align-items:flex-start' },
    h('span', { style: 'flex:none', html: icone.info(15) }),
    h('span', {}, 'O Fincast ainda não envia e-mails. Ao criar um convite você recebe um link para mandar à pessoa pelo canal que preferir.')));

  corpo.appendChild(h('h3', { style: 'margin:0 0 10px' }, `Com acesso (${membros.length})`));
  corpo.appendChild(
    membros.length === 0
      ? vazio('Ninguém tem acesso a esta incorporadora ainda.', botaoConvidar())
      : h('div', { style: 'display:grid;gap:8px;max-width:820px' },
          ...membros.map((m) => linhaMembro(m, empresaId, m.usuarioId === perfil.id, perfil.isAdminHype, podeGerir))),
  );

  if (convites.length > 0) {
    corpo.appendChild(h('h3', { style: 'margin:26px 0 10px' }, `Convites aguardando (${convites.length})`));
    corpo.appendChild(
      h('div', { style: 'display:grid;gap:8px;max-width:820px' },
        ...convites.map((c) => linhaConvite(c, podeConvidar))),
    );
  }

  return {
    trilha: [
      { texto: 'Incorporadoras', rota: { nome: 'incorporadoras' } },
      { texto: empresa.nome, rota: { nome: 'empreendimentos', empresaId } },
      { texto: 'Equipe' },
    ],
    corpo,
    ativo: 'empreendimentos',
    empresaId,
  };
}
